import { useId, useState, type KeyboardEvent, type SyntheticEvent } from 'react';
import { BACKGROUND_COLORS, BORDER_COLORS } from '@/common/presentation/constants/colors';

export interface PriceBookMenuAction {
  label: string;
  onSelect?: () => void;
  disabled?: boolean;
}

const stop = (event: SyntheticEvent) => event.stopPropagation();

/** A native "⋮" overflow menu; actions without a handler are omitted. Escape closes it. */
export function PriceBookActionsMenu({ label, actions }: { label: string; actions: PriceBookMenuAction[] }) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const available = actions.filter((action) => action.onSelect);
  if (available.length === 0) return null;

  const onKeyDown = (event: KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === 'Escape') setOpen(false);
  };

  return (
    <span style={{ position: 'relative', display: 'inline-block' }} onKeyDown={onKeyDown} role="presentation">
      <button
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-canvas-affordance="menu"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
        onDoubleClick={stop}
      >
        ⋮
      </button>
      {open ? (
        <span
          id={menuId}
          role="menu"
          aria-label={label}
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            background: BACKGROUND_COLORS.WHITE,
            border: `1px solid ${BORDER_COLORS.LIGHT}`
          }}
        >
          {available.map((action) => (
            <button
              key={action.label}
              type="button"
              role="menuitem"
              disabled={action.disabled}
              onClick={(event) => {
                event.stopPropagation();
                setOpen(false);
                action.onSelect?.();
              }}
              onDoubleClick={stop}
            >
              {action.label}
            </button>
          ))}
        </span>
      ) : null}
    </span>
  );
}
